import {
  aabbContainsAabb,
  aabbIntersectsAabb,
  polygonContainsPoint,
  segmentIntersectsAabb,
} from '../geometry/intersect';
import type { Aabb2, Point2 } from '../geometry/point';
import {
  closestPointOnSegment,
  pointToSegmentDistanceSq,
} from '../geometry/polyline';
import type { Loop, PickHit, PickOptions, RectRelation } from './types';

/** Target average occupancy per cell. Trades memory against candidates scanned. */
const SEGMENTS_PER_CELL = 4;
/** Ceiling on grid resolution, so a pathological drawing cannot blow up memory. */
const MAX_CELLS_PER_AXIS = 512;

/**
 * Bounds of the loop's chunks that are on visible layers, or `null` when every
 * chunk is hidden.
 *
 * Chunk granularity rather than loop granularity because stitching runs across
 * layers — real drawings scatter one profile over whatever layers the author was
 * on — so "is this loop visible" has no single answer.
 */
const visibleBoundsOf = (
  loop: Loop,
  hiddenLayers: ReadonlySet<number> | undefined,
): Aabb2 | null => {
  if (!hiddenLayers?.size) {
    return loop.bounds;
  }

  let bounds: Aabb2 | null = null;
  loop.chunks.forEach((chunk) => {
    if (hiddenLayers.has(chunk.layer)) {
      return;
    }
    bounds = bounds
      ? [
          Math.min(bounds[0], chunk.bounds[0]),
          Math.min(bounds[1], chunk.bounds[1]),
          Math.max(bounds[2], chunk.bounds[2]),
          Math.max(bounds[3], chunk.bounds[3]),
        ]
      : chunk.bounds;
  });
  return bounds;
};

/** True when any of the loop's chunks sits on a hidden layer. */
const isPartlyHidden = (
  loop: Loop,
  hiddenLayers: ReadonlySet<number> | undefined,
): boolean =>
  Boolean(hiddenLayers?.size) &&
  loop.chunks.some((chunk) => hiddenLayers?.has(chunk.layer));

export type LoopIndex = {
  readonly loops: readonly Loop[];
  /** Loop by id. Ids are positional within one derivation. */
  get(loopId: number): Loop | undefined;
  /** Nearest geometry within `tolerance`, or `null`. */
  pickAt(point: Point2, options: PickOptions): PickHit | null;
  /**
   * The closest point lying **on** any drawn line within `tolerance`, or `null`.
   *
   * Distinct from `pickAt`, which answers "what did I hit". This answers "where
   * exactly on it" — the foot of the perpendicular — which is what the measure
   * tool needs to snap anywhere along a line rather than only to its ends.
   */
  nearestPointOn(
    point: Point2,
    options: PickOptions,
  ): { point: Point2; layer: number; distance: number } | null;
  /**
   * How every loop that the box touches or swallows sits relative to it.
   * Loops that are simply outside are omitted.
   */
  queryRect(
    rect: Aabb2,
    options?: { hiddenLayers?: ReadonlySet<number> },
  ): Map<number, RectRelation>;
};

/**
 * Builds a uniform-grid index over every segment of every loop.
 *
 * A grid rather than a tree because the queries are small-window and very frequent
 * — one per pointer move while hovering — and a grid answers those with array
 * indexing and no traversal. Drawing geometry is also spread fairly evenly, which
 * is the case grids are good at and trees are merely adequate at.
 */
export const buildLoopIndex = (loops: readonly Loop[]): LoopIndex => {
  // ── Flatten every segment, remembering where it came from ──────────────────
  let segmentCount = 0;
  loops.forEach((loop) => {
    loop.chunks.forEach((chunk) => {
      segmentCount += Math.max(0, chunk.points.length - 1);
    });
  });

  const ax = new Float64Array(segmentCount);
  const ay = new Float64Array(segmentCount);
  const bx = new Float64Array(segmentCount);
  const by = new Float64Array(segmentCount);
  const segLoop = new Int32Array(segmentCount);
  const segChunk = new Int32Array(segmentCount);
  const segLayer = new Int32Array(segmentCount);

  let bounds: Aabb2 | null = null;
  let cursor = 0;
  loops.forEach((loop, loopIndex) => {
    bounds = bounds
      ? [
          Math.min(bounds[0], loop.bounds[0]),
          Math.min(bounds[1], loop.bounds[1]),
          Math.max(bounds[2], loop.bounds[2]),
          Math.max(bounds[3], loop.bounds[3]),
        ]
      : loop.bounds;

    loop.chunks.forEach((chunk) => {
      for (let index = 1; index < chunk.points.length; index += 1) {
        ax[cursor] = chunk.points[index - 1][0];
        ay[cursor] = chunk.points[index - 1][1];
        bx[cursor] = chunk.points[index][0];
        by[cursor] = chunk.points[index][1];
        segLoop[cursor] = loopIndex;
        segChunk[cursor] = chunk.index;
        segLayer[cursor] = chunk.layer;
        cursor += 1;
      }
    });
  });

  const byId = new Map<number, Loop>(loops.map((loop) => [loop.id, loop]));

  // ── Lay out the grid ──────────────────────────────────────────────────────
  const worldWidth = bounds ? bounds[2] - bounds[0] : 0;
  const worldHeight = bounds ? bounds[3] - bounds[1] : 0;
  const targetCells = Math.max(1, Math.ceil(segmentCount / SEGMENTS_PER_CELL));
  const span = Math.max(worldWidth, worldHeight);
  const cellSize =
    span > 0
      ? Math.max(span / Math.sqrt(targetCells), span / MAX_CELLS_PER_AXIS)
      : 1;

  const originX = bounds ? bounds[0] : 0;
  const originY = bounds ? bounds[1] : 0;
  const columns = Math.max(
    1,
    Math.min(MAX_CELLS_PER_AXIS, Math.ceil(worldWidth / cellSize) + 1),
  );
  const rows = Math.max(
    1,
    Math.min(MAX_CELLS_PER_AXIS, Math.ceil(worldHeight / cellSize) + 1),
  );

  const columnOf = (x: number) =>
    Math.max(0, Math.min(columns - 1, Math.floor((x - originX) / cellSize)));
  const rowOf = (y: number) =>
    Math.max(0, Math.min(rows - 1, Math.floor((y - originY) / cellSize)));

  const cells: number[][] = Array.from({ length: columns * rows }, () => []);

  for (let segment = 0; segment < segmentCount; segment += 1) {
    // A segment is registered in every cell its bounding box covers: cheap, and
    // never misses a cell the segment actually passes through.
    const columnStart = columnOf(Math.min(ax[segment], bx[segment]));
    const columnEnd = columnOf(Math.max(ax[segment], bx[segment]));
    const rowStart = rowOf(Math.min(ay[segment], by[segment]));
    const rowEnd = rowOf(Math.max(ay[segment], by[segment]));

    for (let row = rowStart; row <= rowEnd; row += 1) {
      for (let column = columnStart; column <= columnEnd; column += 1) {
        cells[row * columns + column].push(segment);
      }
    }
  }

  /**
   * Generation stamps instead of a `Set` per query: a segment straddling several
   * cells would otherwise be scanned once per cell, and these queries run at
   * pointer-move rate.
   */
  const seen = new Int32Array(segmentCount);
  let generation = 0;

  const forEachCandidate = (
    queryBounds: Aabb2,
    visit: (segment: number) => void,
  ) => {
    generation += 1;
    const columnStart = columnOf(queryBounds[0]);
    const columnEnd = columnOf(queryBounds[2]);
    const rowStart = rowOf(queryBounds[1]);
    const rowEnd = rowOf(queryBounds[3]);

    for (let row = rowStart; row <= rowEnd; row += 1) {
      for (let column = columnStart; column <= columnEnd; column += 1) {
        const bucket = cells[row * columns + column];
        for (let index = 0; index < bucket.length; index += 1) {
          const segment = bucket[index];
          if (seen[segment] === generation) {
            continue;
          }
          seen[segment] = generation;
          visit(segment);
        }
      }
    }
  };

  return {
    loops,

    get: (loopId) => byId.get(loopId),

    pickAt(point, { tolerance, hiddenLayers }) {
      const [x, y] = point;
      const toleranceSq = tolerance * tolerance;
      let bestDistanceSq = toleranceSq;
      let bestSegment = -1;

      forEachCandidate(
        [x - tolerance, y - tolerance, x + tolerance, y + tolerance],
        (segment) => {
          if (hiddenLayers?.has(segLayer[segment])) {
            return;
          }
          const distanceSq = pointToSegmentDistanceSq(
            point,
            [ax[segment], ay[segment]],
            [bx[segment], by[segment]],
          );
          if (distanceSq <= bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestSegment = segment;
          }
        },
      );

      if (bestSegment === -1) {
        return null;
      }
      return {
        loopId: loops[segLoop[bestSegment]].id,
        chunkIndex: segChunk[bestSegment],
        distance: Math.sqrt(bestDistanceSq),
      };
    },

    nearestPointOn(point, { tolerance, hiddenLayers }) {
      const [x, y] = point;
      const toleranceSq = tolerance * tolerance;
      let bestDistanceSq = toleranceSq;
      let bestSegment = -1;

      forEachCandidate(
        [x - tolerance, y - tolerance, x + tolerance, y + tolerance],
        (segment) => {
          if (hiddenLayers?.has(segLayer[segment])) {
            return;
          }
          const distanceSq = pointToSegmentDistanceSq(
            point,
            [ax[segment], ay[segment]],
            [bx[segment], by[segment]],
          );
          if (distanceSq <= bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestSegment = segment;
          }
        },
      );

      if (bestSegment === -1) {
        return null;
      }
      return {
        point: closestPointOnSegment(
          point,
          [ax[bestSegment], ay[bestSegment]],
          [bx[bestSegment], by[bestSegment]],
        ),
        layer: segLayer[bestSegment],
        distance: Math.sqrt(bestDistanceSq),
      };
    },

    queryRect(rect, { hiddenLayers } = {}) {
      const relations = new Map<number, RectRelation>();

      // Fully enclosed is a bounds test and nothing more: a polyline's bounds are
      // its extremes, so bounds-inside-box means every point is inside the box.
      // Measured over the *visible* chunks, because a loop may span layers and
      // hidden geometry is not there to be caught.
      loops.forEach((loop) => {
        const bounds = visibleBoundsOf(loop, hiddenLayers);
        if (bounds && aabbContainsAabb(rect, bounds)) {
          relations.set(loop.id, 'enclosed');
        }
      });

      forEachCandidate(rect, (segment) => {
        const loop = loops[segLoop[segment]];
        if (relations.has(loop.id) || hiddenLayers?.has(segLayer[segment])) {
          return;
        }
        if (
          segmentIntersectsAabb(
            [ax[segment], ay[segment]],
            [bx[segment], by[segment]],
            rect,
          )
        ) {
          relations.set(loop.id, 'crossing');
        }
      });

      // Anything still unclassified whose bounds swallow the box may contain it.
      // Checked over all loops rather than grid candidates, because a loop that
      // surrounds the box need not have any segment near it.
      //
      // A partially hidden loop is excluded outright: what is drawn no longer
      // encloses anything, so it cannot be the thing the box is inside of.
      loops.forEach((loop) => {
        if (
          relations.has(loop.id) ||
          !loop.isClosed ||
          isPartlyHidden(loop, hiddenLayers) ||
          !aabbContainsAabb(loop.bounds, rect) ||
          !aabbIntersectsAabb(loop.bounds, rect)
        ) {
          return;
        }
        if (polygonContainsPoint(loop.points, [rect[0], rect[1]])) {
          relations.set(loop.id, 'contains');
        }
      });

      return relations;
    },
  };
};
